import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Building2, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublisherRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    website_url: '',
    contact_email: '',
    proof_url: '',
    additional_info: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Publisher name is required');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit request');
      }

      toast.success('Publisher request submitted! We\'ll review it shortly.');
      setFormData({ name: '', website_url: '', contact_email: '', proof_url: '', additional_info: '' });
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-500" />
            Request New Publisher
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Publisher Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Dark Horse Comics"
              className="bg-gray-800 border-gray-700"
              data-testid="publisher-name-input"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Website URL</label>
            <Input
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              placeholder="https://..."
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Contact Email</label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              placeholder="contact@publisher.com"
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Proof URL</label>
            <Input
              value={formData.proof_url}
              onChange={(e) => setFormData({ ...formData, proof_url: e.target.value })}
              placeholder="Link to evidence (e.g., your work on their site)"
              className="bg-gray-800 border-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">
              Link to proof of your association with this publisher
            </p>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Additional Info</label>
            <Textarea
              value={formData.additional_info}
              onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
              placeholder="Any additional details..."
              className="bg-gray-800 border-gray-700 min-h-[80px]"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="submit-publisher-request"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Component to display user's publisher requests
export const MyPublisherRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/my-publisher-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'merged':
        return 'Merged';
      default:
        return 'Pending Review';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No publisher requests yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((req) => (
        <div
          key={req.id}
          className="p-4 bg-gray-900 border border-gray-800 rounded-lg"
          data-testid={`request-${req.id}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-white">{req.name}</h4>
              <p className="text-sm text-gray-500">
                Submitted {new Date(req.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(req.status)}
              <span className={`text-sm ${
                req.status === 'approved' ? 'text-green-400' :
                req.status === 'rejected' ? 'text-red-400' :
                'text-yellow-400'
              }`}>
                {getStatusText(req.status)}
              </span>
            </div>
          </div>
          {req.admin_notes && req.status === 'rejected' && (
            <p className="mt-2 text-sm text-red-400 bg-red-950/30 p-2 rounded">
              Reason: {req.admin_notes}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default PublisherRequestModal;
